import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '@/context/AuthContext';
import useActionItemBoardSignalR from '@/hooks/useActionItemBoardSignalR';

/**
 * SuggestedMergesBanner Component
 * Displays detected duplicate action item groups with one-click merge capability.
 * Fetches suggested merges from the backend and allows users to quickly merge duplicates.
 * Listens for DuplicatesChanged SignalR events so all users on the same board see live
 * updates — preventing two users from merging the same group simultaneously.
 */
const SuggestedMergesBanner = ({ onMergeComplete, functionId, boardId }) => {
    const { apiFetch, user } = useAuth();
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dismissed, setDismissed] = useState(new Set());
    const [merging, setMerging] = useState(null); // group index being merged

    const fetchSuggestions = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (functionId) params.append('functionId', functionId);
            if (boardId) params.append('boardId', boardId);
            const url = `/api/ActionItems/suggested-merges${params.size > 0 ? `?${params}` : ''}`;
            const res = await apiFetch(url);
            if (res.ok) {
                const data = await res.json();
                if (data.success && Array.isArray(data.data)) {
                    setGroups(data.data);
                    // Clear dismissed set when groups are refreshed — a merge by another
                    // user changes the group list so stale dismissals become irrelevant.
                    setDismissed(new Set());
                }
            }
        } catch {
            // Silent fail — this is a non-critical suggestion feature
        } finally {
            setLoading(false);
        }
    }, [apiFetch, functionId, boardId]);

    useEffect(() => {
        fetchSuggestions();
    }, [fetchSuggestions]);

    // Wire into the board's SignalR group so every user's banner refreshes the moment
    // any user on the same board completes a merge.
    const boardSignalRCallbacks = useCallback(() => ({
        onDuplicatesChanged: () => {
            fetchSuggestions();
        }
    }), [fetchSuggestions]);

    useActionItemBoardSignalR(boardId, boardSignalRCallbacks(), !!boardId);

    const handleMergeGroup = async (group, targetId) => {
        const items = group.items;
        const sourceIds = items.filter(i => i.id !== targetId).map(i => i.id);
        if (sourceIds.length === 0) return;

        setMerging(targetId);
        try {
            for (const sourceId of sourceIds) {
                const res = await apiFetch(`/api/ActionItems/${sourceId}/merge`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        targetActionItemId: targetId,
                        userId: user?.userID || user?.UserID
                    })
                });
                const data = await res.json();
                if (!data.success) {
                    toast.error(`Failed to merge #${sourceId}: ${data.message}`);
                    return;
                }
            }
            toast.success(`Merged ${sourceIds.length} item(s) into #${targetId}`);
            onMergeComplete?.();
            // fetchSuggestions will be triggered automatically via DuplicatesChanged SignalR event
            // for all users including the one who performed the merge
            fetchSuggestions();
        } catch (err) {
            toast.error('Merge failed');
        } finally {
            setMerging(null);
        }
    };

    const handleDismiss = (index) => {
        setDismissed(prev => new Set([...prev, index]));
    };

    const visibleGroups = groups.filter((_, i) => !dismissed.has(i));

    if (loading || visibleGroups.length === 0) return null;

    return (
        <div className="suggested-merges-banner">
            <div className="suggested-merges-header">
                <span className="suggested-merges-icon">🔗</span>
                <strong>Potential Duplicates Detected</strong>
                <span className="suggested-merges-count">{visibleGroups.length} group(s)</span>
            </div>
            <div className="suggested-merges-groups">
                {visibleGroups.map((group, idx) => {
                    const actualIdx = groups.indexOf(group);
                    const items = group.items || [];
                    const oldest = items[0];
                    return (
                        <div key={actualIdx} className="suggested-merge-group">
                            <div className="merge-group-reason">
                                {group.reason}{group.ticketReference ? ` (${group.ticketReference})` : ''}
                            </div>
                            <div className="merge-group-items">
                                {items.map(item => (
                                    <span key={item.id} className="merge-group-item" title={item.description || item.title}>
                                        #{item.id} {item.title || '(no title)'}
                                    </span>
                                ))}
                            </div>
                            <div className="merge-group-actions">
                                <button
                                    className="btn-merge-quick"
                                    onClick={() => handleMergeGroup(group, oldest?.id)}
                                    disabled={merging !== null}
                                >
                                    {merging === oldest?.id ? 'Merging...' : `Merge into #${oldest?.id}`}
                                </button>
                                <button
                                    className="btn-dismiss"
                                    onClick={() => handleDismiss(actualIdx)}
                                >
                                    Dismiss
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default SuggestedMergesBanner;